import React, { FunctionComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Field, FormikErrors, FormikValues } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import {
  proceedToNextFormStep,
  goBack,
} from "../../../../actions/diversityFormActions";
import RadioButton from "../../../shared/publicForms/RadioButton";
import TextField from "../../../shared/publicForms/TextField";
import Asian from "./Asian";
import Black from "./Black";
import Mixed from "./Mixed";
import White from "./White";
import EditButton from "./EditButton";
import { NextButton } from "./NextButton";
import {
  StepWrapper,
  Title,
  RadioButtonGroupWrapper,
  RadioButtonGroup,
  BackButton,
  ButtonsWrapper,
} from "../../styles/diversity-form-styles";

export interface IEthnicityProps {
  values: {
    ethnicGroup?: null | string;
    ethnicSubgroup?: null | string;
  };
  errors: FormikErrors<FormikValues>;
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate: boolean
  ) => void;
  proceedToNextFormStepAction: typeof proceedToNextFormStep;
  goBackAction: typeof goBack;
}

export const Ethnicity: FunctionComponent<IEthnicityProps> = ({
  values,
  errors,
  proceedToNextFormStepAction,
  goBackAction,
  setFieldValue,
}) => {
  const { ethnicGroup, ethnicSubgroup } = values;

  return (
    <StepWrapper data-test-id="ethnicity-step" formStep>
      <Title data-test-id="title">How would you describe your ethnicity?</Title>
      {(ethnicGroup === "Asian" ||
        ethnicGroup === "Black" ||
        ethnicGroup === "Mixed" ||
        ethnicGroup === "White") && (
        <EditButton setFieldValue={setFieldValue} ethnicGroup={ethnicGroup} />
      )}
      {(!ethnicGroup ||
        ethnicGroup === "Arab" ||
        ethnicGroup === "Other" ||
        ethnicGroup === "Prefer not to say") && (
        <RadioButtonGroupWrapper
          role="group"
          aria-labelledby="ethnicity-radio-group"
        >
          <RadioButtonGroup width={185}>
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Arab"
              label="Arab"
              value="Arab"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Asian"
              label="Asian"
              value="Asian"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Black"
              label="Black"
              value="Black"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Mixed"
              label="Mixed"
              value="Mixed"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
          </RadioButtonGroup>
          <RadioButtonGroup width={185}>
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="White"
              label="White"
              value="White"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Other"
              label="Other"
              value="Other"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
            {ethnicGroup === "Other" && (
              <Field
                name="ethnicSubgroup"
                type="text"
                value={ethnicSubgroup || ""}
                placeholder="Please specify"
                as={TextField}
              />
            )}
            <Field
              name="ethnicGroup"
              type="radio"
              aria-label="Prefer not to say"
              label="Prefer not to say"
              value="Prefer not to say"
              onClick={() => setFieldValue("ethnicSubgroup", null, true)}
              as={RadioButton}
            />
          </RadioButtonGroup>
        </RadioButtonGroupWrapper>
      )}
      {(ethnicGroup === "Asian" || ethnicGroup === "Asian - Other") && (
        <Asian values={values} setFieldValue={setFieldValue} errors={errors} />
      )}
      {(ethnicGroup === "Black" || ethnicGroup === "Black - Other") && (
        <Black values={values} setFieldValue={setFieldValue} errors={errors} />
      )}
      {(ethnicGroup === "Mixed" || ethnicGroup === "Mixed - Other") && (
        <Mixed values={values} setFieldValue={setFieldValue} errors={errors} />
      )}
      {(ethnicGroup === "White" || ethnicGroup === "White - Other") && (
        <White values={values} setFieldValue={setFieldValue} errors={errors} />
      )}
      <ButtonsWrapper>
        <BackButton type="button" onClick={goBackAction}>
          <FontAwesomeIcon icon={faChevronLeft} />
          back
        </BackButton>
        <NextButton
          ethnicGroup={ethnicGroup}
          ethnicSubgroup={ethnicSubgroup}
          errors={errors}
          proceedToNextFormStepAction={proceedToNextFormStepAction}
        />
      </ButtonsWrapper>
    </StepWrapper>
  );
};

Ethnicity.propTypes = {
  values: PropTypes.shape({
    ethnicGroup: PropTypes.string,
    ethnicSubgroup: PropTypes.string,
  }).isRequired,
  errors: PropTypes.shape({
    ethnicGroup: PropTypes.string,
    ethnicSubgroup: PropTypes.string,
  }).isRequired,
  proceedToNextFormStepAction: PropTypes.func.isRequired,
  goBackAction: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default connect(null, {
  proceedToNextFormStepAction: proceedToNextFormStep,
  goBackAction: goBack,
})(Ethnicity);
